import { NJToastContainer, NJButton, NJInlineMessage } from '@engie-group/fluid-design-system-react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Table } from "antd";
import './AdminData.css';
import { apiGatewayConfig } from '../../../config';
import { invokeLambda, displayNetworkError } from '../../../api';
import { useOktaAuth } from "@okta/okta-react";
import { useUser } from '../../../context/UserContext';
import Loading from '../../../Loading';
import ModalDataUpload from '../ModalDataUpload/ModalDataUpload';
import { NJAccordion, NJAccordionItem, NJAccordionItemContent, NJAccordionItemHeader } from '@engie-group/fluid-design-system-react';


const expectationsColumns = [
    {
        title: 'Job family',
        dataIndex: 'Job Family',
        key: 'Job Family',
    },
    {
        title: 'Global Employee Level',
        dataIndex: 'Global Employee Level',
        key: 'Global Employee Level',
    },
    {
        title: 'Experience',
        dataIndex: 'Experience',
        key: 'Experience',
    },
    {
        title: 'Country',
        dataIndex: 'Country',
        key: 'Country',
    },
    {
        title: 'Persona',
        dataIndex: 'Persona',
        key: 'Persona',
    },
    {
        title: 'Gender',
        dataIndex: 'Gender',
        key: 'Gender',
    },

    {
        title: 'Expectation 1',
        dataIndex: 'Expectation 1',
        key: 'Expectation 1',
    },
    {
        title: 'Expectation 2',
        dataIndex: 'Expectation 2',
        key: 'Expectation 2',
    },
    {
        title: 'Expectation 3',
        dataIndex: 'Expectation 3',
        key: 'Expectation 3',
    },
    {
        title: 'Expectation 4',
        dataIndex: 'Expectation 4',
        key: 'Expectation 4',
    },
    {
        title: 'Expectation 5',
        dataIndex: 'Expectation 5',
        key: 'Expectation 5',
    },
]

const evpColumns = [
    {
        title: 'Order',
        dataIndex: 'Order',
        key: 'Order',
    },
    {
        title: 'Language',
        dataIndex: 'Language',
        key: 'Language',
    },
    {
        title: 'Company name',
        dataIndex: 'Company Name',
        key: 'Company Name',
    },
    {
        title: 'Subject',
        dataIndex: 'Subject',
        key: 'Subject',
    },
    {
        title: 'Compulsory text',
        dataIndex: 'Compulsory text',
        key: 'Compulsory text',
    },
    {
        title: 'Call to action',
        dataIndex: 'Call to action',
        key: 'Call to action',
    },
]

const narrationColumns = [
    {
        title: 'Expectation',
        dataIndex: 'Expectation',
        key: 'Expectation',
    },
    {
        title: 'Narrative sentences',
        dataIndex: 'Narrative phrases to propose in a job ad',
        key: 'Narrative phrases to propose in a job ad',
    },
]

const filtersColumns = [
    {
        title: 'Country',
        dataIndex: 'Country',
        key: 'Country',
    },
]

const AdminData = ({ active, dataType, keyValuesMode }) => {

    const userInfo = useUser();
    const { oktaAuth } = useOktaAuth();

    const [dataset, setDataset] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [showImportSuccess, setShowImportSuccess] = useState(false);
    const [showImportError, setShowImportError] = useState(false);
    const [importErrorMessage, setImportErrorMessage] = useState('');
    const [importSuccesssMessage, setImportSuccessMessage] = useState('');

    const columns = useMemo(() => {
        switch (dataType) {
            case 'Expectations':
                return expectationsColumns;
            case 'EVP':
                return evpColumns;
            case 'Narration':
                return narrationColumns;
            case 'Filters':
                return filtersColumns;
            default:
                return expectationsColumns;
        }
    }, [dataType]);

    const handleLoadDataset = useCallback(async () => {
        // Prepare request
        const request = {
            action: 'load',
            dataType: dataType
        }
        setIsLoading(true);
        try {
            const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointAdminDataManagement, request, userInfo);
            setDataset(response.dataset);
        } catch (error) {
            console.error('Error invoking Lambda:', error);
            displayNetworkError(error);
        } finally {
            setIsLoading(false);
        }
    }, [oktaAuth, dataType, userInfo]);

    useEffect(() => {
        if (active) {
            handleLoadDataset();
        }
    }, [active, handleLoadDataset]);

    const processUploadedFile = async (file) => {

        // Prepare request
        const request = {
            action: 'process',
            dataType: dataType,
            file: file
        }
        setIsLoading(true);
        try {
            const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointAdminDataManagement, request, userInfo);
            if (response.success) {
                setShowImportSuccess(true);
                setImportSuccessMessage(response.message);
            }
            else {
                setShowImportError(true);
                setImportErrorMessage(response.error);
            }
            handleLoadDataset();
        } catch (error) {
            displayNetworkError(error);
            setShowImportError(true);
            setImportErrorMessage(error.response?.data?.error || error?.message);
            setIsLoading(false);
        } finally {
            // setIsLoading(false);
        }
    }

    return (
        <>
            {isLoading && <Loading text="Please wait..." />}
            <NJToastContainer type="full-width" />
            <ModalDataUpload
                isOpen={isUploadModalOpen}
                id="modalDataUpload"
                oktaAuth={oktaAuth}
                handleProcessFile={(file) => {
                    setIsUploadModalOpen(false);
                    processUploadedFile(file)
                }}
                handleCancel={() => setIsUploadModalOpen(false)}
            />
            <div className='admin-data-content'>

                <div className='admin-data-buttons'>

                    <NJButton
                        label="Reload data"
                        disabled={false}
                        onClick={() => {
                            setShowImportSuccess(false);
                            setShowImportError(false);
                            setIsUploadModalOpen(true)
                        }}
                        icon='upload'
                    />

                    <div>
                        {showImportSuccess &&
                            <NJInlineMessage
                                className='admin-data-message'
                                hasStatus
                                isClosable
                                variant="success"
                                onClose={() => { setShowImportSuccess(false); setImportSuccessMessage(''); }}
                            >
                                <span data-child-name="njInlineMessageTitle">
                                    Successfull import
                                </span>
                                <div>
                                    {importSuccesssMessage}
                                </div>
                            </NJInlineMessage>
                        }
                        {showImportError &&
                            <NJInlineMessage
                                className='admin-data-message'
                                hasStatus
                                isClosable
                                variant="error"
                                onClose={() => { setShowImportError(false); setImportErrorMessage(''); }}
                            >
                                <span data-child-name="njInlineMessageTitle">
                                    Error importing data
                                </span>
                                <div>
                                    {importErrorMessage}
                                </div>
                            </NJInlineMessage>
                        }
                    </div>
                </div>

                <div className='admin-data-grey-box'>
                    <div className='admin-data-search-zone'>
                    </div>
                    <div className='admin-data-table'>
                        {!keyValuesMode &&
                            <Table
                                dataSource={dataset}
                                rowKey="id"
                                pagination={false}
                                columns={columns}
                                sticky={{
                                    offsetHeader: 0,
                                }}
                            />
                        }
                        {keyValuesMode &&
                            <NJAccordion>
                                {dataset.map((item, index) => (
                                    <NJAccordionItem key={index} id={index}>
                                        <NJAccordionItemHeader>
                                            <div className='admin-data-accordion-header'>
                                                {item.key}
                                            </div>
                                        </NJAccordionItemHeader>
                                        {item.values && item.values.map((value, valueIndex) => (
                                            <NJAccordionItemContent className='admin-data-accordion-content'>
                                                <div >
                                                    {value}
                                                </div>
                                            </NJAccordionItemContent>
                                        ))}

                                    </NJAccordionItem>
                                ))}
                            </NJAccordion>
                        }


                    </div>
                </div>
            </div>



        </>
    )
}

export default AdminData;